import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

import { LoadingPage } from "@/components/shared/LoadingPage";
import { PageHeader } from "@/components/shared/PageHeader";
import { supplierKeys } from "@/lib/queryKeys";
import { SupplierForm } from "@/pages/suppliers/SupplierForm";
import type { SupplierFormValues } from "@/pages/suppliers/supplier-form-shared";
import {
  getSupplier,
  updateSupplier,
  type UpdateSupplierPayload,
} from "@/services/supplierService";

function toUpdatePayload(values: SupplierFormValues): UpdateSupplierPayload {
  return {
    legalName: values.legalName.trim(),
    displayName: values.displayName?.trim() || undefined,
    documentType: values.documentType,
    rut: values.rut?.trim() || undefined,
    countryCode: values.countryCode?.trim().toUpperCase() || undefined,
    docRecepExt: values.docRecepExt?.trim() || undefined,
    address: values.address?.trim() || undefined,
    ciudad: values.ciudad?.trim() || undefined,
    departamento: values.departamento?.trim() || undefined,
    email: values.email?.trim() || undefined,
    phone: values.phone?.trim() || undefined,
    defaultCurrency: values.defaultCurrency,
    defaultCategory: values.defaultCategory?.trim() || undefined,
    autoAckCfeOnReceive: values.autoAckCfeOnReceive,
    notes: values.notes?.trim() || undefined,
  };
}

export function EditSupplierPage(): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: supplier, isLoading, isError } = useQuery({
    queryKey: supplierKeys.detail(id ?? ""),
    queryFn: () => getSupplier(id!),
    enabled: Boolean(id),
  });

  const updateMutation = useMutation({
    mutationFn: (values: SupplierFormValues) =>
      updateSupplier(id!, toUpdatePayload(values)),
    onSuccess: (updated) => {
      toast.success(t("suppliers.edit.saved", "Proveedor actualizado."));
      queryClient.invalidateQueries({ queryKey: supplierKeys.all });
      queryClient.invalidateQueries({
        queryKey: supplierKeys.detail(updated.id),
      });
      navigate(`/suppliers/${updated.id}`);
    },
    onError: () => {
      toast.error(
        t(
          "suppliers.edit.error",
          "No pudimos guardar los cambios. Probá de nuevo.",
        ),
      );
    },
  });

  if (!id) {
    return (
      <div className="fx-content">
        <PageHeader
          title={t("suppliers.edit.invalidId", "Proveedor inválido")}
          onBack={() => navigate("/suppliers")}
        />
      </div>
    );
  }

  if (isLoading) return <LoadingPage />;

  if (isError || !supplier) {
    return (
      <div className="fx-content">
        <PageHeader
          title={t("suppliers.edit.title", "Editar proveedor")}
          onBack={() => navigate("/suppliers")}
        />
        <p className="mt-4 text-sm text-muted-foreground">
          {t("suppliers.edit.notFound", "Proveedor no encontrado.")}
        </p>
      </div>
    );
  }

  const initialValues: Partial<SupplierFormValues> = {
    legalName: supplier.legalName ?? "",
    displayName: supplier.displayName ?? "",
    documentType: supplier.documentType,
    rut: supplier.rut ?? "",
    countryCode: supplier.countryCode ?? "UY",
    docRecepExt: supplier.docRecepExt ?? "",
    address: supplier.address ?? "",
    ciudad: supplier.ciudad ?? "",
    departamento: supplier.departamento ?? "",
    email: supplier.email ?? "",
    phone: supplier.phone ?? "",
    defaultCurrency: supplier.defaultCurrency as "USD" | "UYU" | undefined,
    defaultCategory: supplier.defaultCategory ?? "",
    autoAckCfeOnReceive: supplier.autoAckCfeOnReceive,
    notes: supplier.notes ?? "",
  };

  return (
    <div className="space-y-4 fx-content">
      <PageHeader
        title={t("suppliers.edit.title", "Editar proveedor")}
        onBack={() => navigate(`/suppliers/${id}`)}
      />
      <SupplierForm
        initialValues={initialValues}
        submitLabel={t("suppliers.edit.save", "Guardar cambios")}
        isSubmitting={updateMutation.isPending}
        onCancel={() => navigate(`/suppliers/${id}`)}
        onSubmit={(values) => updateMutation.mutate(values)}
      />
    </div>
  );
}
